import { Button, Divider, Typography, useTheme } from '@mui/material';
import { athleteDisplayName } from '@strength/domain';
import {
  AthletePublishedWorkoutLayer,
  BlockPublishedWorkoutLayer,
  findExerciseNameById,
  mergeWorkoutLayers,
} from '@strength/domain-workout';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../components/drawer/drawer';
import { getSortedCyclesAndSessions } from '../../../utils/block-utils';
import { ModifiedPublishedWorkoutLayer } from '../../actuals/types';
import { getChangeTypeMap, Logs } from './activity-log-page';

type ActivityLogDrawerType = {
  log: Logs | null;
  onClose: () => void;
  open: boolean;
};

export const ActivityLogDrawer = ({
  log,
  onClose,
  open,
}: ActivityLogDrawerType) => {
  const {
    palette: { mediumEmphasisTextLight, highEmphasisTextLight },
  } = useTheme();
  const { t } = useTranslation();

  const Label = ({ label }: { label: string }) => (
    <Typography variant="subtitle1Semibold" color={mediumEmphasisTextLight}>
      {label}
    </Typography>
  );

  const Caption = ({ text }: { text: string }) => (
    <Typography variant="body2" color={highEmphasisTextLight}>
      {text}
    </Typography>
  );

  const { data: modifiedPublishedWorkout } =
    trpc.actuals.modifiedPublishedWorkout.getModifiedPublishedWorkout.useQuery(
      {
        id:
          log?.change?.publishedChangedLayers.athleteModifiedPublishedLayerId ||
          '',
      },
      {
        enabled:
          !!log?.change?.publishedChangedLayers.athleteModifiedPublishedLayerId,
      },
    );

  const { data: updatedPublishedBlock } = trpc.block.getPublishedBlock.useQuery(
    {
      publishedBlockId: log?.change?.publishedChangedLayers.blockLayerId || '',
    },
    { enabled: !!log },
  );

  const { data: previousPublishedBlock } =
    trpc.block.getPublishedBlockByVersion.useQuery(
      {
        blockId: updatedPublishedBlock?.blockId || '',
        version: Math.max((updatedPublishedBlock?.version || 1) - 1, 1),
        workoutId: updatedPublishedBlock?.workoutId || '',
      },
      { enabled: !!updatedPublishedBlock },
    );

  const { data: updatedPublishedAthleteBlock } =
    trpc.block.getPublishedAthleteBlock.useQuery(
      {
        publishedBlockId:
          log?.change?.publishedChangedLayers.athleteLayerId || '',
      },
      { enabled: !!log?.change?.publishedChangedLayers.athleteLayerId },
    );

  const { data: previousPublishedAthleteBlock } =
    trpc.block.getPublishedAthleteBlockByVersion.useQuery(
      {
        blockId: updatedPublishedAthleteBlock?.blockId || '',
        version: Math.max((updatedPublishedAthleteBlock?.version || 1) - 1, 1),
        profileId: updatedPublishedAthleteBlock?.profileId || '',
        workoutId: updatedPublishedAthleteBlock?.workoutId || '',
      },
      { enabled: !!updatedPublishedAthleteBlock },
    );

  const getWorkoutForLog = (
    log: Logs | null,
    athleteWorkout: AthletePublishedWorkoutLayer | undefined,
    publishedBlockWorkout: BlockPublishedWorkoutLayer | undefined,
    modifiedPublishedWorkout?: ModifiedPublishedWorkoutLayer,
  ) => {
    if (!log || !athleteWorkout) return null;
    return publishedBlockWorkout
      ? mergeWorkoutLayers(
          publishedBlockWorkout,
          athleteWorkout,
          modifiedPublishedWorkout,
        )
      : mergeWorkoutLayers(athleteWorkout, modifiedPublishedWorkout);
  };

  const previousWorkout = useMemo(
    () =>
      getWorkoutForLog(
        log,
        previousPublishedAthleteBlock,
        previousPublishedBlock,
      ),
    [previousPublishedBlock, previousPublishedAthleteBlock, log],
  );

  const updatedWorkout = useMemo(
    () =>
      getWorkoutForLog(
        log,
        updatedPublishedAthleteBlock,
        updatedPublishedBlock,
        modifiedPublishedWorkout,
      ),
    [
      updatedPublishedBlock,
      updatedPublishedAthleteBlock,
      modifiedPublishedWorkout,
      log,
    ],
  );

  if (!log) return null;

  const CHANGE_TYPE_MAP = getChangeTypeMap(t);

  const { changeType, createdAt, publishedBy, block, team } = log;

  const { sortedSessions, sortedCycles } = getSortedCyclesAndSessions(block);

  // Get the cycle and session labels for the workout
  // if there's a updated published block layer, use its cycle and session
  // else try to use the athlete published layer since it may be an individual workout
  const cycleLabel = updatedPublishedBlock
    ? sortedCycles.find(
        (cycle) => cycle.id === updatedPublishedBlock.cycleSortOrder,
      )?.name
    : updatedPublishedAthleteBlock
      ? `${block.cycleLabel} ${updatedPublishedAthleteBlock.cycleSortOrder}`
      : '';
  const sessionLabel = updatedPublishedBlock
    ? sortedSessions.find(
        (session) => session.id === updatedPublishedBlock.sessionSortOrder,
      )?.name
    : updatedPublishedAthleteBlock
      ? `${block.sessionLabel} ${updatedPublishedAthleteBlock.sessionSortOrder}`
      : '';

  const date = createdAt ? new Date(createdAt) : new Date();

  const workoutChange = () => {
    const workoutExerciseId = log.change?.workoutExerciseId ?? '';
    switch (changeType) {
      case 'tablet_exercise_added':
      case 'exercise_added':
        return (
          <div>
            <Label
              label={
                changeType === 'tablet_exercise_added'
                  ? t('tabletAddedExercise')
                  : t('addedExercise')
              }
            />
            <Caption
              text={
                findExerciseNameById(updatedWorkout, workoutExerciseId) || ''
              }
            />
          </div>
        );
      case 'tablet_exercise_removed':
      case 'exercise_removed':
        return (
          <div>
            <Label label={t('removedExercise')} />
            <Caption
              text={
                findExerciseNameById(previousWorkout, workoutExerciseId) || ''
              }
            />
          </div>
        );
      case 'tablet_exercise_updated':
      case 'exercise_updated': {
        const previousExerciseName = findExerciseNameById(
          previousWorkout,
          workoutExerciseId,
        );
        return (
          <div className="space-y-1">
            {previousExerciseName && (
              <div>
                <Label
                  label={
                    changeType === 'tablet_exercise_updated'
                      ? t('tabletModifiedExercise')
                      : t('modifiedExercise')
                  }
                />
                <Caption text={previousExerciseName} />
              </div>
            )}
            <div>
              <Label label={t('newExercise')} />
              <Caption
                text={
                  findExerciseNameById(updatedWorkout, workoutExerciseId) || ''
                }
              />
            </div>
          </div>
        );
      }
      case 'tablet_prescription_change':
      case 'exercise_prescription_change':
        return (
          <div>
            <Label
              label={
                changeType === 'tablet_prescription_change'
                  ? t('tabletModifiedExercise')
                  : t('modifiedExercise')
              }
            />
            <Caption
              text={
                findExerciseNameById(updatedWorkout, workoutExerciseId) || ''
              }
            />
          </div>
        );
      case 'assigned_date_changed':
        return (
          <div className="space-y-1">
            <div>
              <Label label={t('oldAssignedDate')} />
              <Caption
                text={new Date(
                  previousWorkout?.assignedDate ?? '',
                ).toLocaleDateString(undefined, {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric',
                })}
              />
            </div>
            <div>
              <Label label={t('newAssignedDate')} />
              <Caption
                text={new Date(
                  updatedWorkout?.assignedDate ?? '',
                ).toLocaleDateString(undefined, {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric',
                })}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h3">{CHANGE_TYPE_MAP[changeType]}</Typography>
        <Typography variant="body2" color={mediumEmphasisTextLight}>
          {athleteDisplayName(log.athlete)}
        </Typography>
      </DrawerHeader>
      <Divider />
      <div className="p-6 flex flex-col gap-2 grow">
        <div>
          <Label label={t('date')} />
          <Caption
            text={date.toLocaleString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          />
          <Caption
            text={date.toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          />
        </div>
        <div>
          <Label label={t('team')} />
          <Caption text={team.name} />
        </div>
        <div>
          <Label label={t('trainingBlock')} />
          <Caption text={block.track.name} />
        </div>
        <div>
          <Label label="Workout" />
          <Caption text={`${cycleLabel}, ${sessionLabel}`} />
        </div>
        {workoutChange()}
        <div>
          <Label label="Changed By" />
          <Caption
            text={athleteDisplayName({
              givenName: publishedBy?.givenName ?? '',
              familyName: publishedBy?.familyName ?? '',
            })}
          />
        </div>
      </div>
      <DrawerFooter className="flex flex-row gap-2 w-full p-8 justify-end">
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
